import React, { useState, useEffect } from 'react';
import Cookies from 'universal-cookie';
import ObexRequestHandlerP80 from '../../handlers/ObexRequestsHandlerP80';
import { useToast } from '../../hooks/useToast';
import './Login.css';

import LoginInfo from './components/LoginInfo';
import LoginForm from './components/LoginForm';
import PasswordRecovery from './components/PasswordRecovery';
import ChangePasswordModal from '../../components/shared/Modal/ChangePasswordModal';

interface PendingAuth {
  token: string;
  refreshToken: string;
  user: any;
  currentPassword: string;
}

const Login: React.FC = () => {
  const { notify } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [recovering, setRecovering] = useState(false);

  const [pendingAuth, setPendingAuth] = useState<PendingAuth | null>(null);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('theme') as 'light' | 'dark') || 'dark';
    }
    return 'dark';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    try {
      let response = await ObexRequestHandlerP80.get('/login', {
        email_or_username: email,
        password: password
      });

      if (response.data) response = response.data;

      const loginData = response?.response || response?.result?.response;
      if (response && (response.status === true || response.status === 200) && loginData && loginData.web_token) {
        const { web_token, refresh_token } = loginData;

        let user = null;
        let mustChangePassword = false;

        try {
          let meResponse = await ObexRequestHandlerP80.get('/me', { access_token: web_token });
          if (meResponse && meResponse.data) meResponse = meResponse.data;

          const meData = meResponse?.response ?? meResponse?.result?.response;
          const userObj = meData?.user;
          const rolesList = meData?.roles;

          if (userObj) {
            mustChangePassword =
              userObj.must_change_password === true ||
              userObj.must_change_password === 'true' ||
              userObj.must_change_password === 1 ||
              userObj.must_change_password === '1';

            user = {
              id: userObj.id,
              name: `${userObj.name || ''} ${userObj.last_name || ''}`.trim() || userObj.username,
              email: userObj.email,
              username: userObj.username,
              must_change_password: mustChangePassword,
              role: Array.isArray(rolesList) && rolesList.length > 0
                ? rolesList.map((r: any) => r.name).filter(Boolean).join(', ')
                : undefined,
            };
          }
        } catch (profileError) {
          console.error('Failed to fetch user profile', profileError);
        }

        if (mustChangePassword) {
          setPendingAuth({
            token: web_token,
            refreshToken: refresh_token,
            user,
            currentPassword: password
          });
          setShowChangePasswordModal(true);
          setIsLoading(false);
          notify('Cambio de Contraseña Requerido', 'Por favor actualiza tu contraseña para continuar.', 'warning');
          return;
        }

        const cookies = new Cookies();
        cookies.set('101Obex', JSON.stringify({ token: web_token, refreshToken: refresh_token, user }), { path: '/' });
        window.location.href = '/';
      } else {
        setIsLoading(false);
        notify('Error de inicio de sesión', 'Credenciales inválidas o error al iniciar sesión.', 'warning');
      }
    } catch (error) {
      console.error('Login failed', error);
      setIsLoading(false);
      notify('Error', 'Error al iniciar sesión. Por favor intente nuevamente.', 'urgent');
    }
  };

  const handlePasswordChanged = () => {
    if (!pendingAuth) return;
    const cookies = new Cookies();
    const updatedUser = pendingAuth.user
      ? { ...pendingAuth.user, must_change_password: false }
      : null;
    cookies.set(
      '101Obex',
      JSON.stringify({
        token: pendingAuth.token,
        refreshToken: pendingAuth.refreshToken,
        user: updatedUser
      }),
      { path: '/' }
    );
    setShowChangePasswordModal(false);
    window.location.href = '/';
  };

  const handleCancelPasswordChange = () => {
    setShowChangePasswordModal(false);
    setPendingAuth(null);
    setPassword('');
  };

  return (
    <div className="login-container">
      <LoginInfo />
      {recovering ? (
        <PasswordRecovery
          initialIdentifier={email}
          onBack={() => setRecovering(false)}
          onDone={(message) => {
            setRecovering(false);
            setPassword('');
            notify('Contraseña actualizada', message, 'info');
          }}
        />
      ) : (
        <LoginForm
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          handleLogin={handleLogin}
          isLoading={isLoading}
          onForgot={() => setRecovering(true)}
        />
      )}
      {pendingAuth && (
        <ChangePasswordModal
          isOpen={showChangePasswordModal}
          accessToken={pendingAuth.token}
          initialCurrentPassword={pendingAuth.currentPassword}
          onSuccess={handlePasswordChanged}
          onCancel={handleCancelPasswordChange}
          isMandatory={true}
        />
      )}
    </div>
  );
};

export default Login;
